import React from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import PilotBanner from "@/modules/racket_lab_admin/PilotBanner";
import RLNavSidebar from "@/modules/racket_lab_admin/RLNavSidebar";
import JSONInspector from "@/modules/racket_lab_admin/JSONInspector";
import DispatchStatusBanner from "@/modules/racket_lab_admin/DispatchStatusBanner";
import useAdminApi from "@/hooks/useAdminApi";
import { useRacketLabAdminT } from "@/i18n/racketLabAdminI18n";

const Field = ({ label, value, testId, mono = false }) => (
  <div data-testid={testId} className="flex flex-col gap-0.5">
    <span className="text-[10px] uppercase tracking-wide text-slate-500">{label}</span>
    <span className={`text-sm text-slate-800 break-all ${mono ? "font-mono text-xs" : ""}`}>{value ?? "—"}</span>
  </div>
);

/**
 * RLAdminMockOutboxItem — Sub-sprint C.
 *
 * Read-only viewer for a captured mock-outbox item. Surfaces the
 * provider, template, captured payload + headers. Confirms NP-R1
 * mock-only contract visually via `DispatchStatusBanner`.
 */
const RLAdminMockOutboxItem = () => {
  const t = useRacketLabAdminT();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data, loading, error } = useAdminApi(
    `/admin/notifications/_ops/mock-outbox/${encodeURIComponent(id)}`,
    { skip: !id },
  );

  return (
    <div data-testid="rl-admin-mock-outbox-item-page" className="flex flex-col gap-4">
      <PilotBanner />
      <RLNavSidebar />
      <DispatchStatusBanner />

      <button
        type="button"
        data-testid="rl-mock-outbox-item-back"
        onClick={() => navigate("/admin/racket-lab/notifications/mock-outbox")}
        className="inline-flex w-fit items-center gap-1 text-xs text-slate-600 hover:text-slate-900"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> {t("notifications.mock_outbox.back")}
      </button>

      <header>
        <h1 className="text-xl font-semibold text-slate-900">{t("notifications.mock_outbox.item_title")}</h1>
        <p className="text-xs text-slate-500">{t("notifications.mock_outbox.item.subtitle")}</p>
      </header>

      {loading && <div data-testid="rl-mock-outbox-item-loading" className="rounded-md border border-slate-200 bg-white p-4 text-sm text-slate-500">{t("notifications.mock_outbox.loading")}</div>}
      {!loading && error && (
        <div data-testid="rl-mock-outbox-item-error" className="rounded-md border border-rose-200 bg-rose-50 p-4 text-sm text-rose-800">
          {error.status === 404
            ? t("notifications.mock_outbox.not_found", { id: id?.slice(0, 12) || "" })
            : `${t("notifications.mock_outbox.item.error_prefix")} ${error.status || ""}: ${error.detail || t("notifications.mock_outbox.item.error_unknown")}`}
        </div>
      )}

      {!loading && !error && data && (
        <>
          <section data-testid="rl-mock-outbox-item-card" className="grid grid-cols-1 gap-4 rounded-md border border-slate-200 bg-white p-4 md:grid-cols-2">
            <Field label={t("notifications.mock_outbox.item.fields.id")} value={data.id} testId="rl-mock-outbox-item-field-id" mono />
            <Field label={t("notifications.mock_outbox.item.fields.provider")} value={data.provider} testId="rl-mock-outbox-item-field-provider" />
            <Field label={t("notifications.mock_outbox.item.fields.result")} value={data.result} testId="rl-mock-outbox-item-field-result" />
            <Field label={t("notifications.mock_outbox.item.fields.envelope_id")} value={data.envelope_id} testId="rl-mock-outbox-item-field-envelope" mono />
            <Field label={t("notifications.mock_outbox.item.fields.template")} value={data.template_key} testId="rl-mock-outbox-item-field-template" />
            <Field label={t("notifications.mock_outbox.item.fields.channel")} value={data.channel} testId="rl-mock-outbox-item-field-channel" />
            <Field label={t("notifications.mock_outbox.item.fields.captured_at")} value={data.captured_at} testId="rl-mock-outbox-item-field-captured-at" />
            <Field label={t("notifications.mock_outbox.item.fields.captured_by")} value={data.captured_by_user_id} testId="rl-mock-outbox-item-field-captured-by" mono />
            <Field label={t("notifications.mock_outbox.item.fields.recipient_hash")} value={data.recipient_hash} testId="rl-mock-outbox-item-field-recipient-hash" mono />
            <Field label={t("notifications.mock_outbox.item.fields.redacted_reason")} value={data.redacted_reason} testId="rl-mock-outbox-item-field-redacted" />
          </section>

          <section data-testid="rl-mock-outbox-item-json-section">
            <h2 className="mb-2 text-sm font-medium text-slate-700">{t("notifications.mock_outbox.item.captured_payload_title")}</h2>
            <JSONInspector value={data} dataTestId="rl-mock-outbox-item-json" />
          </section>
        </>
      )}
    </div>
  );
};

export default RLAdminMockOutboxItem;
